import api from './api';
import type { WorkOrder, CreateWorkOrderData, WorkOrderStatus, WorkOrderSummary } from '../types/workOrder';
import type { RoastProfileRow } from '../pages/roasts/component/types';
import { cache } from '../utils/cache';
import { translate } from '../i18n/translate';
import { readLanguagePreferencesFromStorage } from '../features/language/preferences/storage';
import { resolveWorkOrderRoastType } from '../constants/roastTypes';

const t = (key: string, fallback?: string) =>
  translate(readLanguagePreferencesFromStorage().languageCode, key, undefined, fallback);

interface WorkOrderRoastProfileDTO {
  agtron?: {
    wholeBean?: number;
    groundBean?: number;
    classification?: {
      scaa?: string;
      common?: string;
    };
  };
  roast_type?: string;
  stage_summary?: Record<string, { time?: string; temp?: number | string }>;
  profile?: RoastProfileRow[];
  weight_analysis?: {
    shrinkagePercent?: number;
    totalTimeMinutes?: number;
    inputWeight?: number;
    outputWeight?: number;
  };
}

interface WorkOrderDTO {
  id: string;
  company_id: string;
  work_order_number: string;
  recipe_id: string | null;
  recipeName?: string;
  item_id: string | null;
  item_code?: string | null;
  item_name?: string | null;
  quantity_ordered: number;
  quantity_completed: number;
  status: string;
  scheduled_start_date: string;
  actual_start_date?: string;
  actual_end_date?: string;
  notes?: string;
  internal_notes?: string;
  created_at: string;
  updated_at: string;
  inputs?: WorkOrderInputDTO[];
  machine?: string;
  roaster?: string;
  machine_type_id?: string;
  roaster_id?: string;
  order_date?: string;
  order_time?: string;
  input_weight?: number;
  output_weight?: number;
  use_roast_profile?: boolean;
  artisan_profile_id?: string;
  artisan_profile_title?: string;
  roast_type?: string;
  roast_profile?: WorkOrderRoastProfileDTO;
  is_saved_as_recipe?: boolean;
}

interface WorkOrderInputDTO {
  id: string;
  work_order_id: string;
  item_id: string;
  name?: string;
  quantity_required: number;
  quantity_consumed: number;
  stock_before?: number;
  stock_after?: number;
  created_at: string;
  lot_id?: string;
  lot_number?: string;
}

interface WorkOrderCogsResponse {
  work_order_id: string;
  output_quantity: number;
  total_input_cost: number;
  unit_cost: number;
  standard_cost: number;
}

interface WorkOrderAutosavePayload {
  roastProfile?: {
    agtron?: {
      wholeBean?: number;
      groundBean?: number;
      classification?: {
        scaa?: string;
        common?: string;
      };
    };
    roastType?: string;
    stageSummary?: Record<string, { time?: string; temp?: number | string }>;
    profile?: RoastProfileRow[];
    weightAnalysis?: {
      shrinkagePercent?: number;
      totalTimeMinutes?: number;
      inputWeight?: number;
      outputWeight?: number;
    };
  };
  lots: {
    itemId: string;
    lotId: string;
    quantity: number;
  }[];
  roastType?: string;
  artisanProfileId?: string;
  inputWeight?: number;
  outputWeight?: number;
}

interface WorkOrderSummaryDTO {
  id: string;
  work_order_number: string;
  order_date: string | null;
  actual_end_date: string | null;
  item_id: string | null;
  item_name: string;
  input_weight: number;
  output_weight: number;
  roast_type: string;
}

function mapDtoToWorkOrderSummary(dto: WorkOrderSummaryDTO): WorkOrderSummary {
  return {
    id: dto.id,
    workOrderNumber: dto.work_order_number,
    orderDate: dto.order_date,
    actualEndDate: dto.actual_end_date,
    itemId: dto.item_id,
    itemName: dto.item_name ?? '',
    inputWeight: dto.input_weight ?? 0,
    outputWeight: dto.output_weight ?? 0,
    roastType: dto.roast_type ?? '',
  };
}

const extractWorkOrderList = (payload: unknown): WorkOrderDTO[] => {
  if (Array.isArray(payload)) {
    return payload as WorkOrderDTO[];
  }
  if (!payload || typeof payload !== 'object') {
    return [];
  }

  const obj = payload as Record<string, unknown>;
  const candidates = [obj.data, obj.work_orders, obj.workOrders, obj.orders, obj.workorders];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate as WorkOrderDTO[];
    }
  }
  return [];
};

const mapDtoToWorkOrder = (dto: WorkOrderDTO): WorkOrder => {
  // Group inputs by item_id to handle lot splits
  const groupedItems = new Map<string, {
    inventoryItemId: string;
    name?: string;
    quantity: number;
    lots: { lotId: string; lotNumber: string; quantity: number; stockBefore?: number; stockAfter?: number }[];
    isOutside: boolean;
  }>();

  dto.inputs?.forEach(input => {
    // If it's an outside item (no item_id), use name as key
    const key = input.item_id || input.name || 'unknown';
    
    if (!groupedItems.has(key)) {
      groupedItems.set(key, {
        inventoryItemId: input.item_id,
        name: input.name,
        quantity: 0,
        lots: [],
        isOutside: !!input.name && !input.item_id
      });
    }

    const item = groupedItems.get(key)!;
    item.quantity += input.quantity_required;
    
    if (input.lot_id && input.lot_number) {
      item.lots.push({
        lotId: input.lot_id,
        lotNumber: input.lot_number,
        quantity: input.quantity_required,
        stockBefore: input.stock_before,
        stockAfter: input.stock_after,
      });
    }
  });

  return {
    id: dto.id,
    workOrderNumber: dto.work_order_number,
    itemCode: dto.item_code ?? undefined,
    itemName: dto.item_name ?? undefined,
    recipeId: dto.recipe_id,
    recipeName: dto.recipeName || t('workOrders.form.unknownRecipe'),
    status: dto.status as WorkOrderStatus,
    internalNotes: dto.internal_notes,
    items: Array.from(groupedItems.values()).map(item => ({
      inventoryItemId: item.inventoryItemId,
      name: item.name,
      quantity: item.quantity,
      isOutside: item.isOutside,
      lots: item.lots.length > 0 ? item.lots : undefined
    })),
    startDate: dto.actual_start_date,
    endDate: dto.actual_end_date,
    targetDate: dto.scheduled_start_date,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
    machine: dto.machine,
    roaster: dto.roaster,
    machineTypeId: dto.machine_type_id,
    roasterId: dto.roaster_id,
    date: dto.order_date,
    time: dto.order_time,
    itemId: dto.item_id ?? undefined,
    inputWeight: dto.input_weight,
    outputWeight: dto.output_weight,
    useRoastProfile: dto.use_roast_profile,
    artisanProfileId: dto.artisan_profile_id ?? undefined,
    artisanProfileTitle: dto.artisan_profile_title ?? undefined,
    roastType: resolveWorkOrderRoastType(dto.roast_type, dto.roast_profile?.roast_type),
    roastProfile: dto.roast_profile
      ? {
          agtron: dto.roast_profile.agtron
            ? {
                wholeBean: dto.roast_profile.agtron.wholeBean ?? 0,
                groundBean: dto.roast_profile.agtron.groundBean ?? 0,
                classification: {
                  scaa: dto.roast_profile.agtron.classification?.scaa ?? '',
                  common: dto.roast_profile.agtron.classification?.common ?? '',
                },
              }
            : undefined,
          roastType: dto.roast_profile.roast_type,
          stageSummary: dto.roast_profile.stage_summary,
          profile: dto.roast_profile.profile,
          weightAnalysis: dto.roast_profile.weight_analysis,
        }
      : undefined,
    isSavedAsRecipe: dto.is_saved_as_recipe ?? false,
  };
};

export interface ListWorkOrdersParams {
  page?: number;
  size?: number;
  search?: string;
  status?: string;
}

export const workOrderService = {
  getAll: async (): Promise<WorkOrder[]> => {
    const response = await api.get('/work-orders');
    const list = extractWorkOrderList(response.data);
    return list.map(mapDtoToWorkOrder);
  },

  list: async (params: ListWorkOrdersParams = {}): Promise<{ data: WorkOrder[]; total: number }> => {
    const { page = 1, size = 25, search, status } = params;
    const query = new URLSearchParams();
    query.set('limit', String(size));
    query.set('offset', String((page - 1) * size));
    query.set('include_meta', 'true');
    if (search && search.trim()) query.set('search', search.trim());
    if (status && status !== 'all') query.set('status', status.toLowerCase());
    const response = await api.get(`/work-orders?${query.toString()}`);
    const raw = response.data as unknown;
    let total = 0;
    let list: WorkOrderDTO[];
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const obj = raw as Record<string, unknown>;
      list = extractWorkOrderList(obj);
      const meta = obj.meta as { total?: number } | undefined;
      if (typeof meta?.total === 'number') total = meta.total;
      else if (typeof obj.total === 'number') total = obj.total as number;
      else total = list.length;
    } else {
      list = extractWorkOrderList(raw);
      total = list.length;
    }
    return { data: list.map(mapDtoToWorkOrder), total };
  },

  getCompletedByLots: async (lotIds: string[]): Promise<Record<string, WorkOrderSummary[]>> => {
    if (lotIds.length === 0) return {};
    const response = await api.get('/work-orders/by-lots', {
      params: { lot_ids: lotIds.join(','), status: 'completed' },
    });
    const rawByLot = response.data?.data;
    if (!rawByLot || typeof rawByLot !== 'object' || Array.isArray(rawByLot)) {
      return {};
    }
    const out: Record<string, WorkOrderSummary[]> = {};
    for (const [lotId, list] of Object.entries(rawByLot as Record<string, WorkOrderSummaryDTO[]>)) {
      if (!Array.isArray(list)) continue;
      out[lotId] = list.map(mapDtoToWorkOrderSummary);
    }
    return out;
  },

  getById: async (id: string): Promise<WorkOrder | undefined> => {
    return cache.fetch(`workorders_${id}`, async () => {
      // Note: Backend might not have a dedicated single-fetch endpoint for frontend yet,
      // but we can reuse the list or add one. For now, let's assume we fetch all or add endpoint.
      // The summary mentioned adding GetWorkOrder to backend, so let's assume /work-orders/:id exists or we filter.
      // Actually, backend has GetWorkOrders (plural). Let's use that for now if specific endpoint missing,
      // or better, assume standard REST /work-orders/:id.
      // Backend handler shows: UpdateWorkOrder at /:id, but no GetWorkOrder exposed in router yet?
      // Let's verify router.
      const response = await api.get<WorkOrderDTO>(`/work-orders/${id}`);
      return mapDtoToWorkOrder(response.data);
    });
  },

  create: async (data: CreateWorkOrderData): Promise<WorkOrder> => {
    const payload = {
      machine: data.machine,
      roaster: data.roaster,
      machine_type_id: data.machineTypeId,
      roaster_id: data.roasterId,
      date: data.date,
      time: data.time,
      item_id: data.itemId,
      input_weight: data.inputWeight,
      output_weight: data.outputWeight,
      use_roast_profile: data.useRoastProfile,
      artisan_profile_id: data.artisanProfileId,
      roast_type: data.roastType ?? data.roastProfile?.roastType ?? undefined,
      onboarding_sample: data.onboardingSample,
      lots: data.lots.map(lot => ({
        item_id: lot.itemId,
        lot_id: lot.lotId,
        quantity: lot.quantity,
      })),
      roast_profile: data.useRoastProfile && data.roastProfile
        ? {
            agtron: data.roastProfile.agtron,
            roast_type: data.roastProfile.roastType,
            stage_summary: data.roastProfile.stageSummary,
            profile: data.roastProfile.profile,
            weight_analysis: data.roastProfile.weightAnalysis,
          }
        : undefined,
      save_as_new_recipe: data.saveAsNewRecipe,
      internal_notes: data.internalNotes,
    };

    const response = await api.post<WorkOrderDTO>('/work-orders', payload);
    cache.invalidate('workorders');
    cache.invalidate('reports_');
    // WO mutations move stock between GB lots and RB items, so any cached
    // inventory snapshot is now stale.
    cache.invalidate('inventory');
    return mapDtoToWorkOrder(response.data);
  },

  update: async (id: string, data: CreateWorkOrderData): Promise<WorkOrder> => {
    const payload = {
      machine: data.machine,
      roaster: data.roaster,
      machine_type_id: data.machineTypeId,
      roaster_id: data.roasterId,
      date: data.date,
      time: data.time,
      item_id: data.itemId,
      input_weight: data.inputWeight,
      output_weight: data.outputWeight,
      use_roast_profile: data.useRoastProfile,
      artisan_profile_id: data.artisanProfileId,
      roast_type: data.roastType ?? data.roastProfile?.roastType ?? undefined,
      lots: data.lots
        ? data.lots.map(lot => ({
            item_id: lot.itemId,
            lot_id: lot.lotId,
            quantity: lot.quantity,
          }))
        : undefined,
      roast_profile: data.useRoastProfile && data.roastProfile
        ? {
            agtron: data.roastProfile.agtron,
            roast_type: data.roastProfile.roastType,
            stage_summary: data.roastProfile.stageSummary,
            profile: data.roastProfile.profile,
            weight_analysis: data.roastProfile.weightAnalysis,
          }
        : undefined,
      save_as_new_recipe: data.saveAsNewRecipe,
      internal_notes: data.internalNotes,
    };

    const response = await api.put<WorkOrderDTO>(`/work-orders/${id}`, payload);
    cache.delete(`workorders_${id}`);
    cache.delete('workorders_all');
    cache.invalidate('reports_');
    cache.invalidate('inventory');
    return mapDtoToWorkOrder(response.data);
  },

  /**
   * Bulk scheduling: persist an incomplete card's basic info and bean choice
   * without touching stock or the roast quota. The backend deducts stock
   * whenever `lots` is present regardless of status, and re-checks the quota
   * whenever `input_weight` is present — so neither field is sent here.
   */
  updateDraftBasics: async (
    id: string,
    data: {
      machine?: string;
      roaster?: string;
      machineTypeId?: string;
      roasterId?: string;
      date: string;
      time: string;
      itemId?: string;
      internalNotes?: string;
    },
  ): Promise<WorkOrder> => {
    const response = await api.put<WorkOrderDTO>(`/work-orders/${id}`, {
      machine: data.machine,
      roaster: data.roaster,
      machine_type_id: data.machineTypeId,
      roaster_id: data.roasterId,
      date: data.date,
      time: data.time,
      item_id: data.itemId || undefined,
      internal_notes: data.internalNotes,
      use_roast_profile: false,
    });
    cache.delete(`workorders_${id}`);
    cache.delete('workorders_all');
    return mapDtoToWorkOrder(response.data);
  },

  complete: async (id: string, expiryDate?: string): Promise<WorkOrder> => {
    const body = expiryDate ? { expiry_date: expiryDate } : {};
    const response = await api.post<WorkOrderDTO>(`/work-orders/${id}/complete`, body);
    cache.delete(`workorders_${id}`);
    cache.delete('workorders_all');
    cache.invalidate('reports_');
    cache.invalidate('inventory');
    return mapDtoToWorkOrder(response.data);
  },

  updateStatus: async (id: string, status: WorkOrderStatus, expiryDate?: string): Promise<WorkOrder> => {
    const body: Record<string, unknown> = { status };
    if (expiryDate) body.expiry_date = expiryDate;
    const response = await api.patch<WorkOrderDTO>(`/work-orders/${id}/status`, body);
    cache.delete(`workorders_${id}`);
    cache.delete('workorders_all');
    cache.invalidate('reports_');
    cache.invalidate('inventory');
    return mapDtoToWorkOrder(response.data);
  },

  autosave: async (id: string, payload: WorkOrderAutosavePayload): Promise<WorkOrder> => {
    const requestBody = {
      roast_profile: payload.roastProfile
        ? {
            agtron: payload.roastProfile.agtron,
            roast_type: payload.roastProfile.roastType,
            stage_summary: payload.roastProfile.stageSummary,
            profile: payload.roastProfile.profile,
            weight_analysis: payload.roastProfile.weightAnalysis,
          }
        : undefined,
      lots: payload.lots?.map(lot => ({
        item_id: lot.itemId,
        lot_id: lot.lotId,
        quantity: lot.quantity,
      })),
      roast_type: payload.roastType,
      artisan_profile_id: payload.artisanProfileId,
      input_weight: payload.inputWeight,
      output_weight: payload.outputWeight,
    };

    const response = await api.patch<WorkOrderDTO>(`/work-orders/${id}/autosave`, requestBody);
    cache.delete(`workorders_${id}`);
    cache.delete('workorders_all');
    cache.invalidate('reports_');
    return mapDtoToWorkOrder(response.data);
  },

  unlinkArtisanProfile: async (id: string): Promise<WorkOrder> => {
    const response = await api.patch<WorkOrderDTO>(`/work-orders/${id}/unlink-artisan-profile`, {});
    cache.delete(`workorders_${id}`);
    cache.delete('workorders_all');
    cache.invalidate('reports_');
    return mapDtoToWorkOrder(response.data);
  },

  updateRoastType: async (id: string, roastType: string): Promise<WorkOrder> => {
    const response = await api.patch<WorkOrderDTO>(`/work-orders/${id}/roast-type`, { roast_type: roastType });
    cache.delete(`workorders_${id}`);
    cache.delete('workorders_all');
    cache.invalidate('reports_');
    return mapDtoToWorkOrder(response.data);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/work-orders/${id}`);
    cache.delete(`workorders_${id}`);
    cache.delete('workorders_all');
    cache.invalidate('reports_');
    cache.invalidate('inventory');
  },

  autoCalculateLots: async (
    itemId: string,
    inputWeight: number
  ): Promise<{ lots: { lotId: string; remaining: number; quantity: number }[] }> => {
    const response = await api.post('/work-orders/auto-calculate-lots', {
      item_id: itemId,
      input_weight: inputWeight,
    });

    const data = response.data || {};
    const rawLots = Array.isArray(data.lots)
      ? data.lots
      : Array.isArray(data.data?.lots)
        ? data.data.lots
        : [];

    const lots = rawLots.map((lot: {
      lotId?: string;
      lot_id?: string;
      id?: string;
      remaining?: number;
      remaining_quantity?: number;
      stockBefore?: number;
      stock_before?: number;
      quantity?: number;
      usedWeight?: number;
      used_weight?: number;
      weight?: number;
    }) => ({
      lotId: lot.lotId ?? lot.lot_id ?? lot.id ?? '',
      remaining:
        lot.remaining ??
        lot.remaining_quantity ??
        lot.stockBefore ??
        lot.stock_before ??
        0,
      quantity: lot.quantity ?? lot.usedWeight ?? lot.used_weight ?? lot.weight ?? 0,
    }));

    return { lots };
  },

  getRoastCogs: async (
    workOrderId: string
  ): Promise<WorkOrderCogsResponse> => {
    const response = await api.get<WorkOrderCogsResponse>(`/work-orders/${workOrderId}/cogs`);
    return response.data;
  }
};

